import readingLevels from '../../../../public/json/reading-levels.json';

export const HOVER_OUTLINE_CLASS =
  'group hover:outline outline-1 hover:outline-offset-4 focus-within:outline-offset-4 hover:outline-gray-400 focus-within:outline  focus-within:outline-emerald-500 focus-within:hover:outline-emerald-500 rounded-[1px] print:outline-none print:hover:outline-none';

export const isJSONString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

// `.gdocs-remove` marks DOM that exists for the app, not for the document: the off-screen
// measurement rigs (utils/Measured.jsx), hover overlays, and print-only table spacers. The
// export scraper already strips it (diffit-lambda diffit-to-slides/slides-generator/utils/
// browser-functions.js, preparePageContent). The clipboard's HTML flavor comes from outerHTML,
// which — unlike the innerText used for the plain-text flavor — is not layout-aware, so without
// this an activity's invisible measurement copy gets pasted as a second summary / question set /
// vocab list.
//
// `[data-copy-exclude]` marks on-screen chrome that must never be copied even though it is visibly
// rendered — a slide deck's speaker-notes row, whose collapsed preview holds the full first line of
// a teacher's private note (the visual truncation is CSS-only). Because it is genuinely visible,
// layout-awareness does not save the plain-text flavor either, which is why that flavor is now read
// from this cleaned clone as well.
//
// `[data-speaker-notes]` is the hidden, export-only copy of those notes. It is invisible today via a
// bare `hidden` attribute, and copyComputedStyles deliberately drops computed `none` values, so the
// copied HTML carries no inline `display:none` fallback — a paste target that ignores the attribute
// would surface the note. Removing it outright is safer than relying on that.
//
// Stripping here cannot affect exports: this only ever mutates a detached clone in the user's
// browser, while the lambda reads `[data-speaker-notes]` off the live DOM in its own session.
const stripNonContentNodes = (clone) => {
  clone
    .querySelectorAll('.gdocs-remove, [data-copy-exclude], [data-speaker-notes]')
    .forEach((node) => node.remove());
};

export const copyActivityContainerHTML = async (blockUniqueId) => {
  const container = document.getElementById(`activity-content-${blockUniqueId}`);
  if (!container) {
    console.warn(`Could not find activity container for block ${blockUniqueId}`);
    return false;
  }

  // Function to copy computed styles from source element to target element
  const copyComputedStyles = (sourceElement, targetElement) => {
    const computedStyles = window.getComputedStyle(sourceElement);

    // Properties to skip - these are either not useful for copy/paste or cause issues
    const skipProperties = new Set([
      // Webkit internal properties
      '-webkit-text-fill-color',
      '-webkit-text-stroke-width',
      '-webkit-text-stroke-color',
      // Animation and transition properties
      'animation',
      'animation-delay',
      'animation-direction',
      'animation-duration',
      'animation-fill-mode',
      'animation-iteration-count',
      'animation-name',
      'animation-play-state',
      'animation-timing-function',
      'transition',
      'transition-delay',
      'transition-duration',
      'transition-property',
      'transition-timing-function',
      // Transform properties that might not translate well
      'transform',
      'transform-origin',
      'transform-style',
      // Cursor and user interaction
      'cursor',
      'pointer-events',
      'user-select',
      // Content that shouldn't be copied
      'content',
      // Grid and flex properties that might not work in Google Docs
      'grid-template-columns',
      'grid-template-rows',
      'grid-area',
      'flex-basis',
      'flex-grow',
      'flex-shrink',
      'order',
      // Z-index often doesn't translate well
      'z-index',
      // Outline is usually for focus states
      'outline',
      'outline-color',
      'outline-style',
      'outline-width',
      'outline-offset',
    ]);

    // Build inline style string by iterating through all computed properties
    let inlineStyles = '';

    // Get all property names
    for (let i = 0; i < computedStyles.length; i++) {
      const prop = computedStyles[i];

      // Skip if in our skip list
      if (skipProperties.has(prop)) continue;

      const value = computedStyles.getPropertyValue(prop);

      // Skip empty values
      if (!value || value === '') continue;

      // Skip vendor-prefixed properties (except for some we might want to keep)
      if (
        prop.startsWith('-webkit-') ||
        prop.startsWith('-moz-') ||
        prop.startsWith('-ms-') ||
        prop.startsWith('-o-')
      ) {
        continue;
      }

      // Filter out common default values to reduce noise
      const isLikelyDefault =
        value === 'none' ||
        value === 'auto' ||
        value === 'initial' ||
        value === 'inherit' ||
        value === 'unset' ||
        value === '0' ||
        value === 'normal' ||
        value === 'visible' ||
        value === 'static' ||
        value === 'transparent' ||
        value === 'rgba(0, 0, 0, 0)' ||
        // Skip default display values based on element type
        (prop === 'display' && value === 'block' && targetElement.tagName === 'DIV') ||
        (prop === 'display' &&
          value === 'inline' &&
          ['SPAN', 'A', 'B', 'I', 'EM', 'STRONG'].includes(targetElement.tagName)) ||
        (prop === 'display' && value === 'table-cell' && targetElement.tagName === 'TD') ||
        (prop === 'display' && value === 'table-row' && targetElement.tagName === 'TR') ||
        (prop === 'display' && value === 'table' && targetElement.tagName === 'TABLE') ||
        // Skip position static as it's the default
        (prop === 'position' && value === 'static') ||
        // Skip default opacity
        (prop === 'opacity' && value === '1') ||
        // Skip 0px margins and paddings (but keep other 0px values like borders)
        ((prop.startsWith('margin') || prop.startsWith('padding')) && value === '0px');

      if (!isLikelyDefault) {
        inlineStyles += `${prop}: ${value}; `;
      }
    }

    // Apply the inline styles, preserving any existing inline styles
    if (inlineStyles) {
      targetElement.style.cssText = inlineStyles + (targetElement.style.cssText || '');
    }
  };

  // Function to recursively clone and apply styles
  const cloneWithStyles = (sourceElement, targetElement) => {
    // Copy computed styles from source to target
    copyComputedStyles(sourceElement, targetElement);

    // Process all child elements
    const sourceChildren = sourceElement.children;
    const targetChildren = targetElement.children;

    for (let i = 0; i < sourceChildren.length; i++) {
      if (targetChildren[i]) {
        cloneWithStyles(sourceChildren[i], targetChildren[i]);
      }
    }
  };

  try {
    // Clone the container
    const clonedContainer = container.cloneNode(true);

    // Remove any print-specific classes and hover states
    const elementsWithClasses = clonedContainer.querySelectorAll('[class]');
    elementsWithClasses.forEach((el) => {
      // Handle both string className (HTML elements) and SVGAnimatedString (SVG elements)
      let classStr = '';
      if (typeof el.className === 'string') {
        classStr = el.className;
      } else if (el.className && el.className.baseVal !== undefined) {
        // SVG elements have className as an SVGAnimatedString object
        classStr = el.className.baseVal;
      }

      if (classStr) {
        const classes = classStr
          .split(' ')
          .filter(
            (cls) =>
              !cls.includes('print:') &&
              !cls.includes('hover:') &&
              !cls.includes('focus:') &&
              !cls.includes('group')
          );

        // Set the className appropriately
        if (typeof el.className === 'string') {
          el.className = classes.join(' ');
        } else if (el.className && el.className.baseVal !== undefined) {
          el.className.baseVal = classes.join(' ');
        }
      }
    });

    // Apply computed styles from original to clone
    cloneWithStyles(container, clonedContainer);

    // Must run AFTER cloneWithStyles: that walk pairs source and clone children by index, so
    // pruning first would shift the clone's indices and apply each element's styles to the wrong
    // sibling (the measurement wrapper's visibility:hidden landing on real content).
    stripNonContentNodes(clonedContainer);

    // Create the HTML content with inline styles
    const htmlContent = clonedContainer.outerHTML;

    // Read the plain-text flavor from the cleaned clone rather than the live container, so that
    // `[data-copy-exclude]` chrome is excluded from BOTH flavors. innerText needs layout to stay
    // layout-aware, hence the brief offscreen attach; outerHTML is captured above first so these
    // positioning styles never reach the copied HTML. (The fallback path below already copies from
    // an offscreen-attached clean clone, so this brings the two paths into agreement.)
    clonedContainer.style.position = 'fixed';
    clonedContainer.style.left = '-99999px';
    clonedContainer.style.top = '0';
    let plainTextContent;
    document.body.appendChild(clonedContainer);
    try {
      plainTextContent = clonedContainer.innerText;
    } finally {
      // Detach even if the read throws, so a failure can't leave the clone in the document (the
      // catch below then retries via the fallback path, which attaches a clone of its own).
      clonedContainer.remove();
    }

    // Use the modern clipboard API to copy HTML
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const textBlob = new Blob([plainTextContent], { type: 'text/plain' });

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      }),
    ]);

    return true;
  } catch (error) {
    // Fallback for browsers that don't support clipboard.write()
    try {
      // Create a temporary element with inline styles
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      // Clone and process the container
      const clonedContainer = container.cloneNode(true);

      // Remove any print-specific classes and hover states from clone
      const elementsWithClasses = clonedContainer.querySelectorAll('[class]');
      elementsWithClasses.forEach((el) => {
        // Handle both string className (HTML elements) and SVGAnimatedString (SVG elements)
        let classStr = '';
        if (typeof el.className === 'string') {
          classStr = el.className;
        } else if (el.className && el.className.baseVal !== undefined) {
          // SVG elements have className as an SVGAnimatedString object
          classStr = el.className.baseVal;
        }

        if (classStr) {
          const classes = classStr
            .split(' ')
            .filter(
              (cls) =>
                !cls.includes('print:') &&
                !cls.includes('hover:') &&
                !cls.includes('focus:') &&
                !cls.includes('group')
            );

          // Set the className appropriately
          if (typeof el.className === 'string') {
            el.className = classes.join(' ');
          } else if (el.className && el.className.baseVal !== undefined) {
            el.className.baseVal = classes.join(' ');
          }
        }
      });

      // Apply computed styles from original to clone using the same functions defined above
      copyComputedStyles(container, clonedContainer);

      // Recursively apply styles to all children
      const sourceChildren = container.querySelectorAll('*');
      const targetChildren = clonedContainer.querySelectorAll('*');

      sourceChildren.forEach((sourceEl, index) => {
        if (targetChildren[index]) {
          copyComputedStyles(sourceEl, targetChildren[index]);
        }
      });

      // After the style pass, for the same index-pairing reason as the primary path above.
      stripNonContentNodes(clonedContainer);

      tempDiv.appendChild(clonedContainer);

      // Create a range and select the content
      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Copy the selection
      const success = document.execCommand('copy');
      selection.removeAllRanges();

      // Clean up
      document.body.removeChild(tempDiv);

      return success;
    } catch (fallbackError) {
      console.error('Failed to copy content:', fallbackError);
      return false;
    }
  }
};

export const getScrollParent = (element) => {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/auto|scroll/.test(overflow + overflowY)) return parent;
    parent = parent.parentElement;
  }
  return window;
};

export const scrollBlockIntoView = (
  block,
  offset = 60,
  behavior = 'smooth',
  skipScrollIfOnScreen = false
) => {
  if (!block || !block.getBoundingClientRect) return false;
  const elementPosition = block.getBoundingClientRect().top;
  const scrollParent = getScrollParent(block);
  const isWindow = scrollParent === window;
  const containerTop = isWindow ? 0 : scrollParent.getBoundingClientRect().top;
  const offsetPosition = elementPosition - containerTop - offset;

  if (skipScrollIfOnScreen) {
    const isVisibleOnTop = elementPosition + block.offsetHeight > 0;
    const isVisibleOnBottom = elementPosition + block.offsetHeight > window.scrollY;
    if (isVisibleOnTop || isVisibleOnBottom) {
      return true;
    }
  }
  // if the block is already on the screen, no need to scroll

  return new Promise((resolve) => {
    // Set a timeout to return true after 2 seconds
    const timeoutId = setTimeout(() => {
      resolve(true);
    }, 2000);

    const getScrollTop = () => (isWindow ? window.scrollY : scrollParent.scrollTop);
    const targetScrollY = getScrollTop() + offsetPosition;
    scrollParent.scrollBy({
      top: offsetPosition,
      behavior,
    });

    // If behavior is 'auto' or 'instant', resolve immediately
    if (behavior === 'auto' || behavior === 'instant') {
      clearTimeout(timeoutId);
      resolve(true);
      return;
    }

    // For smooth scrolling, wait for the animation to complete
    const checkScrollEnd = () => {
      if (Math.abs(getScrollTop() - targetScrollY) < 1) {
        clearTimeout(timeoutId);
        resolve(true);
      } else {
        requestAnimationFrame(checkScrollEnd);
      }
    };
    requestAnimationFrame(checkScrollEnd);
  });
};

export const PACKET_HEADER_HEIGHT = '68px';

const BLOCKS_TO_SCREENSHOT = [
  'bubble_map_with_image',
  'bubble_map_with_summary',
  'flow_chart_map',
  'character_traits_says_feels_does',
];

export const shouldScreenshotBlock = (block) => {
  return BLOCKS_TO_SCREENSHOT.includes(block.canonical_id);
};

export const getS3IconUrl = (iconName, format = 'svg') => {
  return `https://static.diffit.me/packet_icons/${iconName}.${format}`;
};

export const addReadingLevelNamesToPacket = (packet) => {
  const readingLevel = readingLevels.find(
    (readingLevel) => readingLevel.id === packet.reading_level_id
  );
  if (!readingLevel) {
    return packet;
  }
  return {
    ...packet,
    reading_level_name: readingLevel.name,
    reading_level_id: readingLevel.id,
  };
};

export const getTitleImageFromBlocks = (blocks) => {
  let titleImage = null;
  blocks.forEach((block) => {
    if (!block.resource_data) {
      return;
    }
    if (Object.keys(block.resource_data)?.includes('title_image')) {
      const selectedImageIndex =
        block.resource_data.title_image?.items?.[0]?.selected_image_index ?? 0;
      titleImage =
        block.resource_data.title_image?.items?.[0]?.images[selectedImageIndex]?.image_url;
    }
  });
  return titleImage;
};

export const isCustomActivity = (canonicalId) => {
  return canonicalId?.startsWith('custom_activity');
};
